import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  CheckCircle2,
  CircleAlert,
  ExternalLink,
  Info,
  LoaderCircle,
  TriangleAlert,
  X,
} from "lucide-react";

import {
  ToastContext,
  type ToastApi,
  type ToastInput,
  type ToastRecord,
  type ToastUpdate,
} from "@web/toasts/toast";

const maximumToasts = 5;
let nextToastId = 0;

function defaultDuration(toast: Pick<ToastInput, "durationMs" | "variant">) {
  if (toast.durationMs !== undefined) return toast.durationMs;
  if (toast.variant === "loading") return 0;
  if (toast.variant === "error") return 10_000;
  return 6_500;
}

function ToastIcon({ variant }: Pick<ToastRecord, "variant">) {
  if (variant === "success") return <CheckCircle2 aria-hidden="true" />;
  if (variant === "error") return <CircleAlert aria-hidden="true" />;
  if (variant === "warning") return <TriangleAlert aria-hidden="true" />;
  if (variant === "loading") {
    return <LoaderCircle className="toast-spinner" aria-hidden="true" />;
  }
  return <Info aria-hidden="true" />;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const toastsRef = useRef<ToastRecord[]>([]);
  const timers = useRef(new Map<string, number>());

  const commit = useCallback((next: ToastRecord[]) => {
    toastsRef.current = next;
    setToasts(next);
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      const timer = timers.current.get(id);
      if (timer !== undefined) window.clearTimeout(timer);
      timers.current.delete(id);
      commit(toastsRef.current.filter((toast) => toast.id !== id));
    },
    [commit],
  );

  const schedule = useCallback(
    (id: string, toast: Pick<ToastInput, "durationMs" | "variant">) => {
      const current = timers.current.get(id);
      if (current !== undefined) window.clearTimeout(current);
      timers.current.delete(id);
      const duration = defaultDuration(toast);
      if (duration > 0) {
        timers.current.set(
          id,
          window.setTimeout(() => dismiss(id), duration),
        );
      }
    },
    [dismiss],
  );

  const show = useCallback(
    (toast: ToastInput) => {
      const duplicate = toast.dedupeKey
        ? toastsRef.current.find((item) => item.dedupeKey === toast.dedupeKey)
        : undefined;
      const id = duplicate?.id || `toast-${Date.now()}-${nextToastId++}`;
      const record = { ...toast, id };
      const withoutCurrent = toastsRef.current.filter((item) => item.id !== id);
      const next = [...withoutCurrent, record].slice(-maximumToasts);
      const retained = new Set(next.map((item) => item.id));
      for (const [timerId, timer] of timers.current) {
        if (!retained.has(timerId)) {
          window.clearTimeout(timer);
          timers.current.delete(timerId);
        }
      }
      commit(next);
      schedule(id, record);
      return id;
    },
    [commit, schedule],
  );

  const update = useCallback(
    (id: string, updateValue: ToastUpdate) => {
      const existing = toastsRef.current.find((toast) => toast.id === id);
      if (!existing) return;
      const nextToast: ToastRecord = {
        ...existing,
        ...updateValue,
        action:
          updateValue.action === null
            ? undefined
            : updateValue.action || existing.action,
      };
      commit(
        toastsRef.current.map((toast) =>
          toast.id === id ? nextToast : toast,
        ),
      );
      schedule(id, nextToast);
    },
    [commit, schedule],
  );

  useEffect(
    () => () => {
      for (const timer of timers.current.values()) window.clearTimeout(timer);
      timers.current.clear();
    },
    [],
  );

  const api = useMemo<ToastApi>(
    () => ({ dismiss, show, update }),
    [dismiss, show, update],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <section className="toast-viewport" aria-label="Notifications">
        {toasts.map((toast) => (
          <article
            className="toast"
            data-variant={toast.variant}
            key={toast.id}
            role={toast.variant === "error" ? "alert" : "status"}
            aria-atomic="true"
          >
            <span className="toast-icon">
              <ToastIcon variant={toast.variant} />
            </span>
            <div className="toast-copy">
              <strong>{toast.title}</strong>
              {toast.description && <p>{toast.description}</p>}
              {toast.action && (
                <a href={toast.action.href} target="_blank" rel="noreferrer">
                  {toast.action.label} <ExternalLink size={12} />
                </a>
              )}
            </div>
            {toast.variant === "loading" ? (
              <span className="toast-close-placeholder" aria-hidden="true" />
            ) : (
              <button
                className="toast-close"
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label={`Dismiss ${toast.title}`}
              >
                <X size={15} />
              </button>
            )}
          </article>
        ))}
      </section>
    </ToastContext.Provider>
  );
}
